"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NexusObject = void 0;
const three_1 = require("three");
const objects_1 = require("../objects");
const nexus_1 = require("./nexus");
class NexusObject extends three_1.Mesh {
    constructor(url, renderer, render, onLoad, tokenProvider, material) {
        super(new three_1.BufferGeometry(), material);
        this.autoMaterial = false;
        this.onAfterRender = (renderer, scene, camera, geometry, material, group) => {
            const context = this.context;
            const s = renderer.getSize(new three_1.Vector2());
            context.updateView([0, 0, s.width, s.height], camera.projectionMatrix.elements, this.modelViewMatrix.elements);
            const program = renderer.getContext().getParameter(context.gl.CURRENT_PROGRAM);
            context.webGLRendererAttributes.position = renderer.getContext().getAttribLocation(program, "position");
            context.webGLRendererAttributes.normal = renderer.getContext().getAttribLocation(program, "normal");
            context.webGLRendererAttributes.color = renderer.getContext().getAttribLocation(program, "color");
            context.webGLRendererAttributes.uv = renderer.getContext().getAttribLocation(program, "uv");
            context.render();
        };
        this.geometry.setAttribute('position', new three_1.BufferAttribute(new Float32Array(3), 3));
        this.autoMaterial = !material;
        this.frustumCulled = false;
        const gl = renderer.getContext();
        this.context = nexus_1.Nexus.getContext(gl);
        this.context.tokenProvider = tokenProvider;
        this.load(url, onLoad);
        this.context.onUpdate = function () { render(); };
        this.onAfterRender = this.onAfterRender;
    }
    raycast(raycaster, intersects) {
        if (!this.model) {
            throw new Error("Model is undefined");
        }
        const model = this.model;
        const sphere = this.computeBoundingSphere(model);
        sphere.applyMatrix4(this.matrixWorld);
        const point = new three_1.Vector3();
        let intersect = raycaster.ray.intersectSphere(sphere, point);
        if (!intersect) {
            return;
        }
        const invertedMatrixWorld = new three_1.Matrix4().copy(this.matrixWorld).invert();
        const ray = new three_1.Ray();
        ray.copy(raycaster.ray).applyMatrix4(invertedMatrixWorld);
        const intersection = {
            distance: -1,
            point: intersect,
            object: this
        };
        let nodeLenght = model.nodes.length;
        let nodes = model.nodes;
        let nodesToProcess = [];
        for (let i = 0; i < nodeLenght; i++) {
            if (i == nodeLenght - 1 && nodesToProcess.length != 0 && nodes[i].status === objects_1.NexusNodeDataStatus.ABSENT) {
                // console.log("nodes to proces at end", nodesToProcess.length)
                this.computeMeshIntersections(nodesToProcess, raycaster, ray, intersection);
            }
            if (nodes[i].status !== objects_1.NexusNodeDataStatus.PRESENT) {
                continue;
            }
            if (this.model.mode !== "POINT") {
                nodesToProcess.push(nodes[i]);
            }
            else {
                this.computePointCloudIntersection(nodes[i], raycaster, ray, intersection);
            }
        }
        if (intersection.distance === -1) {
            return;
        }
        intersects.push(intersection);
    }
    load(url, onLoad) {
        this.context.load(url).then((model) => {
            this.model = model;
            this.geometry.boundingBox = this.computeBoundingBox(model);
            this.geometry.boundingSphere = this.computeBoundingSphere(model);
            // Model positioning is just for test
            // const position = this.geometry.boundingBox.getCenter(new Vector3()).negate();
            // this.position.set(position.x, position.y, position.z);
            if (this.autoMaterial) {
                console.log('mesh automaterial');
                this.material = new three_1.MeshLambertMaterial({ color: 0xffffff });
            }
            if (model.vertex.normal) {
                console.log('vertex normals present');
                this.geometry.setAttribute('normal', new three_1.BufferAttribute(new Float32Array(3), 3));
            }
            if (model.vertex.color) {
                console.log('vertex colors present');
                this.geometry.setAttribute('color', new three_1.BufferAttribute(new Float32Array(4), 4));
                if (this.autoMaterial && model.mode === 'POINT') {
                    console.log('switching to points material for point cloud');
                    this.material = new three_1.PointsMaterial({ vertexColors: true, size: 0.01, sizeAttenuation: true });
                }
            }
            if (model.vertex.texCoord) {
                console.log('vertex tex coordinates present');
                this.geometry.setAttribute('uv', new three_1.BufferAttribute(new Float32Array(2), 2));
                if (this.autoMaterial) {
                    console.log('mesh lambert with mapped texture');
                    const texture = new three_1.DataTexture(new Uint8Array([1, 1, 1]), 1, 1, three_1.RGBAFormat);
                    texture.needsUpdate = true;
                    this.material = new three_1.MeshLambertMaterial({ color: 0xffffff, map: texture });
                }
            }
            if (model.face.index) {
                console.log('vertex indices present');
                this.geometry.setIndex(new three_1.BufferAttribute(new Uint32Array(3), 3));
            }
            onLoad(this);
        });
    }
    computeBoundingBox(model) {
        const min = new three_1.Vector3(+Infinity, +Infinity, +Infinity);
        const max = new three_1.Vector3(-Infinity, -Infinity, -Infinity);
        for (const node of model.nodes) {
            const nodePointsToSink = node.patches.length && node.patches[0].node.sink;
            if (!nodePointsToSink) {
                continue;
            }
            const sphere = node.sphere;
            min.x = Math.min(min.x, sphere.x - sphere.tightRadius);
            min.y = Math.min(min.y, sphere.y - sphere.tightRadius);
            min.z = Math.min(min.z, sphere.z - sphere.tightRadius);
            max.x = Math.max(max.x, sphere.x + sphere.tightRadius);
            max.y = Math.max(max.y, sphere.y + sphere.tightRadius);
            max.z = Math.max(max.z, sphere.z + sphere.tightRadius);
        }
        return new three_1.Box3(min, max);
    }
    computeBoundingSphere(model) {
        const sphere = model.sphere;
        const center = new three_1.Vector3(sphere.x, sphere.y, sphere.z);
        const radius = sphere.radius;
        return new three_1.Sphere(center, radius);
    }
    fillVertexCoordinates(verticesData, index, resultVertex) {
        resultVertex.set(verticesData[index + 0], verticesData[index + 1], verticesData[index + 2]);
    }
    computeMeshIntersections(nodes, raycaster, ray, intersection) {
        if (!this.model) {
            throw new Error("Model is undefined");
        }
        const point = new three_1.Vector3();
        let faceVertexA = new three_1.Vector3();
        let faceVertexB = new three_1.Vector3();
        let faceVertexC = new three_1.Vector3();
        for (let i = 0; i < nodes.length; i++) {
            const vert = nodes[i].verticesBuffer;
            const face = nodes[i].indicesBuffer;
            if (vert === undefined || face === undefined) {
                return;
            }
            // Do ray intersect node sphere
            const sphere = new three_1.Sphere();
            sphere.center.set(nodes[i].sphere.x, nodes[i].sphere.y, nodes[i].sphere.z);
            sphere.radius = nodes[i].sphere.radius;
            if (!raycaster.ray.intersectSphere(sphere, point)) {
                return;
            }
            ;
            for (let index = 0; index < face.length; index += 3) {
                this.fillVertexCoordinates(vert, face[index + 0] * 3, faceVertexA);
                this.fillVertexCoordinates(vert, face[index + 1] * 3, faceVertexB);
                this.fillVertexCoordinates(vert, face[index + 2] * 3, faceVertexC);
                // TODO: use material to determine if using doubleface or not!
                const hit = ray.intersectTriangle(faceVertexC, faceVertexB, faceVertexA, false, point);
                if (!hit) {
                    continue;
                }
                ;
                // Check distances in world space
                hit.applyMatrix4(this.matrixWorld);
                const worldSpaceDistance = hit.distanceTo(raycaster.ray.origin);
                if (worldSpaceDistance < raycaster.near || worldSpaceDistance > raycaster.far) {
                    continue;
                }
                ;
                if (intersection.distance === -1 || worldSpaceDistance < intersection.distance) {
                    intersection.distance = worldSpaceDistance;
                    intersection.point = hit.clone();
                }
            }
        }
    }
    computePointCloudIntersection(node, raycaster, ray, intersection) {
        if (!this.model) {
            throw new Error("Model is undefined");
        }
        const sphere = new three_1.Sphere();
        let pointVertex = new three_1.Vector3();
        const vert = node.verticesBuffer;
        if (vert === undefined) {
            return;
        }
        for (let index = 0; index < vert.length; index += 3) {
            this.fillVertexCoordinates(vert, index, pointVertex);
            sphere.center.set(pointVertex.x, pointVertex.y, pointVertex.z);
            sphere.radius = 0.1;
            // TODO: use material to determine if using doubleface or not!
            const hit = ray.intersectSphere(sphere, pointVertex);
            if (!hit) {
                continue;
            }
            ;
            // Check distances in world space
            hit.applyMatrix4(this.matrixWorld);
            const worldSpaceDistance = hit.distanceTo(raycaster.ray.origin);
            if (worldSpaceDistance < raycaster.near || worldSpaceDistance > raycaster.far) {
                continue;
            }
            ;
            if (intersection.distance === -1 || worldSpaceDistance < intersection.distance) {
                intersection.distance = worldSpaceDistance;
                intersection.point = hit.clone();
            }
        }
    }
}
exports.NexusObject = NexusObject;
